import React, { useEffect, useMemo, useState } from 'react';

import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useQueryClient } from '@tanstack/react-query';
import pick from 'lodash/pick';
import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Typography,
} from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import CopyableField from '~/components/Common/CopyableField';
import ErrorListDisplay from '~/components/Common/ErrorListDisplay';
import { addValuesToSchema } from '~/components/Common/Forms/helpers/schemaPopulator';
import { buttonMessages, buttonStates } from '~/components/Common/Forms/states/muiSubmitButton';
import CustomCheckboxWidget from '~/components/Common/Forms/widgets/CustomCheckboxWidget';
import CustomSelectFieldWidget from '~/components/Common/Forms/widgets/CustomSelectFieldWidget';
import CustomTextFieldWidget from '~/components/Common/Forms/widgets/CustomTextFieldWidget';
import { ManifestPackagesModal, ManifestStatuses } from '~/components/Common/Modals/ManifestPackagesModal';
import SetBrandRegionOverridesModal, {
  BrandRegionOverrides,
} from '~/components/Common/Modals/SetBrandRegionOverridesModal';
import SetRegionModal from '~/components/Common/Modals/SetRegionModal';
import { getPackageRoomTypeMatch } from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/autofillPackage';
import { getRoomRateLabel } from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/utils';
import CopyToPackageModal from '~/components/Offers/Edit/CopyToPackageModal';

import { offerQueryKeys } from '~/hooks/apis/offers/useGetOffer';
import { useUpdatePackage } from '~/hooks/apis/offers/useUpdatePackage';

import PackagesService from '~/services/PackagesService';

import {
  getBrandRegionOverridesDisplay,
  getRegionNameFromCode,
  normalizeBrandRegionOverrides,
} from '~/utils/brandRegionOverrides';

import PriceList from './PriceList';
import {
  VISIBLE_SCHEMA_FIELDS,
  filterFlashRoomRates,
  findRoomTypeByOrder,
  getAvailabilityRatesUrl,
  getDealOptionLabel,
  getPackageSaveErrorMessages,
  getRatePlanUrl,
  getRoomTypeContentUrl,
  overlayVisiblePackageFields,
  pickVisiblePackageSchema,
} from './utils';

const WIDGETS = {
  CheckboxWidget: CustomCheckboxWidget,
  TextWidget: CustomTextFieldWidget,
  SelectWidget: CustomSelectFieldWidget,
};

function buildUiSchema(roomTypeContentUrl: string | undefined, ratePlanUrl: string | undefined) {
  return {
    'ui:order': [...VISIBLE_SCHEMA_FIELDS],
    deal_option_name: { 'ui:disabled': true },
    campaign_code: { 'ui:emptyValue': '' },
    fk_room_type_id: roomTypeContentUrl
      ? {
          'ui:help': (
            <MuiLink component={Link} to={roomTypeContentUrl} target="_blank" underline="always" variant="caption">
              View Room Type
            </MuiLink>
          ),
        }
      : {},
    fk_room_rate_id: ratePlanUrl
      ? {
          'ui:help': (
            <MuiLink component={Link} to={ratePlanUrl} target="_blank" underline="always" variant="caption">
              View Rate Plan
            </MuiLink>
          ),
        }
      : {},
  };
}

function injectRoomTypesAndRates(schema: RJSFSchema, property: App.Property, roomTypeId: string): RJSFSchema {
  let nextSchema = pickVisiblePackageSchema(schema) as Parameters<typeof addValuesToSchema>[2];
  if (property?.room_types_count) {
    nextSchema = addValuesToSchema('fk_room_type_id', property.room_types, nextSchema);
  }
  const roomType = property?.room_types?.find((type) => type.id === roomTypeId);
  if (roomType?.room_rates?.length) {
    nextSchema = addValuesToSchema(
      'fk_room_rate_id',
      roomType.room_rates.map((rate) => ({ id: rate.id, name: getRoomRateLabel(rate) })),
      nextSchema,
    );
  }
  return nextSchema;
}

interface Props {
  packageData: App.AccommodationPackage;
  packageSchema: RJSFSchema;
  property: App.Property;
  offer: App.Offer;
  packageIndex: number;
  allSave: number;
  onSaveComplete: () => void;
  onSaveFailed: (dealOptionLabel: string) => void;
}

export default function LinkDealOptionForm({
  packageData,
  packageSchema,
  property,
  offer,
  packageIndex,
  allSave,
  onSaveComplete,
  onSaveFailed,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const updatePackageMutation = useUpdatePackage();
  const [formData, setFormData] = useState(packageData);
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [saveErrors, setSaveErrors] = useState<Array<string>>([]);
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const [brandOverridesModalOpen, setBrandOverridesModalOpen] = useState(false);
  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [copyToModalOpen, setCopyToModalOpen] = useState(false);
  const [copyToState, setCopyToState] = useState(buttonStates.default);
  const [copyToError, setCopyToError] = useState<Array<string>>([]);

  const roomTypeContentUrl = getRoomTypeContentUrl(formData, offer.id_salesforce_external);
  const selectedRoomRate = property?.room_types
    ?.find((roomType) => roomType.id === formData.fk_room_type_id)
    ?.room_rates?.find((rate) => rate.id === formData.fk_room_rate_id);
  const ratePlanUrl = getRatePlanUrl(formData, selectedRoomRate?.rate_plan?.id, offer.id_salesforce_external);

  const uiSchema = useMemo(() => buildUiSchema(roomTypeContentUrl, ratePlanUrl), [roomTypeContentUrl, ratePlanUrl]);
  const schema = useMemo(
    () => injectRoomTypesAndRates(packageSchema, property, formData.fk_room_type_id),
    [packageSchema, property, formData.fk_room_type_id],
  );

  async function handleSave(nextFormData: App.AccommodationPackage, fromSaveAll = false) {
    setSaveState(buttonStates.saving);
    setSaveErrors([]);
    const packageId = nextFormData.le_package_id ?? nextFormData.id_salesforce_external;
    const offerId = offer.id_salesforce_external;
    const dealOptionLabel = getDealOptionLabel(nextFormData);
    try {
      await updatePackageMutation.mutateAsync({
        offerId,
        packageId,
        packageData: {
          ...pick(nextFormData, VISIBLE_SCHEMA_FIELDS),
          regions: nextFormData.regions,
          brand_region_overrides: normalizeBrandRegionOverrides(nextFormData.brand_region_overrides),
        },
      });
      setSaveState(buttonStates.saved);
      onSaveComplete();
      if (!fromSaveAll) {
        enqueueSnackbar(`Saved ${dealOptionLabel}`, { variant: 'success' });
      }
    } catch (saveError) {
      const messages = getPackageSaveErrorMessages(saveError);
      setSaveState(buttonStates.failed);
      setSaveErrors(messages);
      onSaveFailed(`${dealOptionLabel}: ${messages.join('; ')}`);
      // Save All lists failures in the tab alert; skip per-form toasts so they don't stack.
      if (!fromSaveAll) {
        enqueueSnackbar(`Error saving ${dealOptionLabel}: ${messages.join('; ')}`, { variant: 'error' });
      }
    }
  }

  // Save All triggers every form via a rising `allSave` counter (see LinkDealOptionsTab).
  useEffect(() => {
    if (allSave) {
      handleSave(formData, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSave]);

  const brandRegionOverrideEntries = getBrandRegionOverridesDisplay(formData.brand_region_overrides);
  const hasAllRegions = formData.regions.length === libRegions.getRegions().length;
  const availabilityRatesUrl = getAvailabilityRatesUrl(formData, offer.id_salesforce_external);

  function autofillFromDealOption(manifestType: ManifestStatuses) {
    const roomTypes = property?.room_types ?? [];
    const roomType =
      manifestType === ManifestStatuses.BY_TYPE_ORDER
        ? findRoomTypeByOrder(packageIndex, roomTypes)
        : getPackageRoomTypeMatch({ offerPackage: formData, packageName: formData.deal_option_name, property });
    const roomRates = filterFlashRoomRates(roomType?.room_rates ?? []);
    if (!roomType || !roomRates.length) {
      enqueueSnackbar('No matching room type and rate found for this deal option', { variant: 'warning' });
      return;
    }
    setFormData((current) => ({
      ...current,
      name: roomType.name,
      fk_room_type_id: roomType.id,
      fk_room_rate_id: roomRates[0].id,
      package_options: roomRates.map((rate) => ({ fk_room_rate_id: rate.id, name: getRoomRateLabel(rate) })),
      room_occupancy: 'Two Adults',
      flexible_nights: true,
      status: 'draft',
      regions: ['world'],
      number_of_nights: current.number_of_nights || 1,
      max_extra_nights: current.max_extra_nights || 29,
    }));
  }

  async function handleCopyToPackageSubmit(selectedPackages, selectedSchema) {
    setCopyToState(buttonStates.saving);
    setCopyToError([]);
    const selectedFields = selectedSchema.map((field) => field.value);
    const offerId = offer.id_salesforce_external;
    const results = await Promise.all(
      selectedPackages.map((item) => {
        const targetPackage = (offer.packages ?? []).find(
          (pkg) => (pkg.le_package_id ?? pkg.id_salesforce_external) === item.value,
        );
        return PackagesService.updatePackage(
          { ...targetPackage, ...pick(formData, selectedFields) },
          offerId,
          item.value,
        ).catch((error) => error);
      }),
    );
    const erroredUpdates = results.filter((result) => result?.errors);
    if (erroredUpdates.length === 0) {
      setCopyToState(buttonStates.saved);
      await queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
      return;
    }
    setCopyToState(buttonStates.failed);
    setCopyToError(erroredUpdates.map((result) => result.errors[0].message));
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <IconButton onClick={() => setCopyToModalOpen(true)} size="large" color="primary" aria-label="Copy Offer">
            <ContentCopyIcon />
          </IconButton>
          {availabilityRatesUrl ? (
            <MuiLink component={Link} to={availabilityRatesUrl} target="_blank" underline="always">
              View Availability &amp; Rates
            </MuiLink>
          ) : (
            'Missing Data To Get Availability & Rates'
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <strong>IDs</strong>
          <CopyableField value={formData.le_package_id} />
          <CopyableField value={formData.id_salesforce_external} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <Button onClick={() => setManifestModalOpen(true)}>Autofill Package by deal option</Button>
      </Box>
      <Form
        schema={schema}
        uiSchema={uiSchema}
        formData={formData}
        showErrorList={false}
        liveValidate
        validator={validator}
        widgets={WIDGETS}
        className="package-form"
        onChange={(edit) => setFormData((current) => overlayVisiblePackageFields(current, edit.formData))}
        onSubmit={() => handleSave(formData)}
      >
        <Box className="regions-tag-container">
          <Box>
            <label className="control-label">Package Regions</label>
          </Box>
          <Box>
            <ul className="region-tags-list">
              {hasAllRegions ? (
                <li key="world">{getRegionNameFromCode('world')}</li>
              ) : (
                formData.regions.map((region) => <li key={region}>{getRegionNameFromCode(region)}</li>)
              )}
            </ul>
          </Box>
          <Box>
            <Button variant="contained" onClick={() => setRegionModalOpen(true)}>
              Set Regions
            </Button>
          </Box>
        </Box>
        <Box className="regions-tag-container">
          <Box>
            <label className="control-label">Brand Region Overrides</label>
          </Box>
          <Box>
            {brandRegionOverrideEntries.length ? (
              <ul className="region-tags-list">
                {brandRegionOverrideEntries.map(({ brand, brandName, regions }) => (
                  <li key={brand}>
                    <strong>{brandName}: </strong>
                    {regions.join(', ')}
                  </li>
                ))}
              </ul>
            ) : (
              <Box>No overrides set</Box>
            )}
          </Box>
          <Box>
            <Button variant="contained" onClick={() => setBrandOverridesModalOpen(true)}>
              Set Brand Overrides
            </Button>
          </Box>
        </Box>

        {formData.prices && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                Available in currency{' '}
                <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                  ({formData.prices.length} currencies)
                </Box>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PriceList prices={formData.prices} />
            </AccordionDetails>
          </Accordion>
        )}
        {formData.bundle_prices && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                Available in currency — Bundle &amp; Save{' '}
                <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                  ({formData.bundle_prices.length} currencies)
                </Box>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PriceList prices={formData.bundle_prices} />
            </AccordionDetails>
          </Accordion>
        )}

        {saveState === buttonStates.failed && (
          <ErrorListDisplay messages={saveErrors.length ? saveErrors : ['Could not save this deal option']} />
        )}

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" type="submit" color={saveState}>
            {buttonMessages[saveState]}
          </Button>
        </Box>
      </Form>

      <ManifestPackagesModal
        showModal={manifestModalOpen}
        offerType={offer.type}
        onClose={() => setManifestModalOpen(false)}
        onSave={(manifestType) => {
          setManifestModalOpen(false);
          autofillFromDealOption(manifestType);
        }}
      />
      <CopyToPackageModal
        isOpen={copyToModalOpen}
        onHide={() => {
          setCopyToModalOpen(false);
          setCopyToError([]);
          setCopyToState(buttonStates.default);
        }}
        packageData={formData}
        uiSchema={uiSchema}
        schema={schema}
        allPackagesData={offer.packages ?? []}
        onCopyToPackageSubmit={handleCopyToPackageSubmit}
        apiErrors={copyToError}
        copyToState={copyToState}
        setButtonToDefault={() => setCopyToState(buttonStates.default)}
      />
      <SetRegionModal
        open={regionModalOpen}
        onRequestClose={() => setRegionModalOpen(false)}
        onSet={(regions) => {
          setFormData((current) => ({ ...current, regions }));
          setRegionModalOpen(false);
        }}
        regions={formData.regions}
        name="Package Regions"
        label="Tag all regions that you want this package to be visible in"
      />
      <SetBrandRegionOverridesModal
        open={brandOverridesModalOpen}
        onRequestClose={() => setBrandOverridesModalOpen(false)}
        onSet={(brandRegionOverrides: BrandRegionOverrides) => {
          setFormData((current) => ({ ...current, brand_region_overrides: brandRegionOverrides }));
          setBrandOverridesModalOpen(false);
        }}
        brandRegionOverrides={formData.brand_region_overrides ?? null}
        name="Brand Region Overrides"
        label="Tag each brand with the regions that should override package visibility"
        saveButton="Set Brand Overrides"
      />
    </Box>
  );
}
